import { debounce, throttle } from 'lodash';
import { RefObject, useEffect, useLayoutEffect, useMemo, useState } from 'react';

export type UseElementSizeOptions = {
    minWidth?: number;
    minHeight?: number;
    wait?: number;
    /**
     * When not set, children of the element are momentarily hidden to get the size of the container on its own.
     * When true, children's contribution to the size is included in the measurement.
     */
    measureWithChildren?: boolean;
    /**
     * When not set, resizes are debounced and no new size is reported until the resizing settles.
     * When true, resizes are still throttled to reduce redrawing burden, but will fire periodically during the resize.
     */
    responsive?: boolean;
};

export const useElementSize = (
    ref: RefObject<HTMLElement>,
    {
        minWidth = 0,
        minHeight = 0,
        wait = 300,
        measureWithChildren = false,
        responsive = false,
    }: UseElementSizeOptions = {}
) => {
    const [width, setWidth] = useState(minWidth);
    const [height, setHeight] = useState(minHeight);

    const onResize = useMemo(
        () =>
            (responsive ? throttle : debounce)(
                () => {
                    if (!ref.current) return;

                    const oldDisplayStyles: { style: CSSStyleDeclaration; display: string }[] = [];

                    if (!measureWithChildren) {
                        // first hide the children so we can measure the container on its own
                        ref.current.childNodes.forEach((c) => {
                            if ('style' in c) {
                                const style = c.style as CSSStyleDeclaration;
                                oldDisplayStyles.push({ style, display: style.display });
                                style.display = 'none';
                            }
                        });
                    }

                    setWidth(Math.max(minWidth, ref.current?.clientWidth ?? 0));
                    setHeight(Math.max(minHeight, ref.current?.clientHeight ?? 0));

                    if (!measureWithChildren) {
                        // put the children back to how they were
                        oldDisplayStyles.forEach(({ style, display }) => {
                            style.display = display;
                        });
                    }
                },
                wait,
                { leading: true, trailing: true }
            ),
        [responsive, ref, measureWithChildren, wait, minWidth, minHeight]
    );

    /*
     * Store the current DOM element in state so the effect below reruns when it changes. The ref object
     * itself stays the same when a collapsed section mounts a new container, but ref.current points to
     * the new element.
     */
    const [element, setElement] = useState<HTMLElement | null>(null);

    /*
     * Run on every commit. React assigns ref.current during commit and does not rerender, so no dependency
     * array can catch that change. The guard calls setElement only when ref.current changes, so the effect
     * costs one comparison per commit.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        if (element !== ref.current) {
            setElement(ref.current);
        }
    });

    useLayoutEffect(() => {
        if (!element) {
            return;
        }

        onResize();

        /*
         * onResize sets each child's display to none, reads clientWidth and clientHeight, then restores
         * display, all in one synchronous call. ResizeObserver runs its callback after layout, once per
         * frame. By then display is back, so the observer never sees the children hidden and onResize does
         * not retrigger itself.
         */
        const observer = new ResizeObserver(() => onResize());
        observer.observe(element);
        return () => observer.disconnect();
    }, [element, onResize]);

    useEffect(() => {
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            // a pending trailing call would outlive the component, retaining this closure and everything it captured
            onResize.cancel();
        };
    }, [onResize]);

    return { width, height };
};
